import { Box, Typography, useTheme } from '@mui/joy';
import { Chapter, ChapterOptions, Link } from '../../types';
import { DotsIndicator } from '../DotsIndicator/DotsIndicator';
import { NavButton } from './NavButton';
import { Link as ScrollLink, scroller } from 'react-scroll';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../../hooks';
import { useRoute } from '../../providers/RouteProvider';
interface SideNavProps extends Chapter {
	links: Link[];
	activeIndex?: number;
}

const SideNavItem = ({
	link,
	chapter,
	active
}: {
	link: Link;
	chapter: ChapterOptions;
	active: boolean;
}) => {
	const theme = useTheme();

	const matches = useMediaQuery(theme.breakpoints.up('smallLaptop'));
	return (
		<Box
			sx={{
				marginTop: '16px',
				height: '32px',
				whiteSpace: 'nowrap'
			}}
		>
			<Box
				sx={{
					padding: '4px 8px',
					backgroundColor: {
						sm: 'none',
						lg: active ? 'rgba(250, 250, 250, 0.9)' : 'none'
					},
					borderRadius: {
						sm: 'none',
						lg: active ? '5px' : 'none'
					}
				}}
			>
				<ScrollLink
					style={{
						display: 'flex',
						justifyContent: 'start',
						alignItems: 'center',
						cursor: 'pointer',
						textDecoration: 'none'
					}}
					to={link.href}
					smooth='easeInOutCubic'
					offset={matches ? -156 : 0}
					duration={500}
				>
					<Box
						sx={{
							padding: '4px 8px',
							backgroundColor: {
								sm: 'none',
								lg: active ? 'rgba(250, 250, 250, 0.9)' : 'none'
							},
							borderRadius: {
								sm: 'none',
								lg: active ? '5px' : 'none'
							},
							display: 'flex',
							alignItems: 'center',
							'&:hover': {
								'& .MuiTypography-root': {
									opacity: 0.7, // Change the opacity value as needed
									backgroundColor: 'rgba(250, 250, 250, 0.9)' // Using rgba to set opacity
								}
							},
							pointerEvents: { xs: 'none', lg: 'auto' }
						}}
					>
						<DotsIndicator
							chapter={chapter}
							diameter='21px'
							type={active ? 'solid' : 'clear'}
							isActive={active}
							placeHolder
						/>{' '}
						<Typography
							level='body2'
							sx={{
								lineHeight: '1rem',
								marginLeft: '6px',
								fontWeight: 'bold',
								opacity: { sm: active ? 0 : 0, lg: active ? 1 : 0 },
								':hover': {
									backgroundColor: theme.palette.background.surface,
									opacity: active ? 1 : 0.7
								}
							}}
						>
							{link.label}
						</Typography>
					</Box>
				</ScrollLink>
			</Box>
		</Box>
	);
};

export const SideNav = ({ links, chapter, activeIndex }: SideNavProps) => {
	const [currentNavIndex, setCurrentNavIndex] = useState(0);
	const [isAtEndOfPage, setIsAtEndOfPage] = useState(false);
	const theme = useTheme();
	const {
		sectionAnchor,
		selectedState,
		election,
		congressTab,
		seat,
		raceFilter,
		setSectionAnchor
	} = useRoute();
	const [lastScrollState, setLastScrollState] = useState(selectedState);
	const [lastScrollElection, setLastScrollElection] = useState(election);
	const [lastScrollCongressTab, setLastScrollCongressTab] =
		useState(congressTab);
	const [lastScrollSeat, setLastScrollSeat] = useState(seat[0]);
	const [lastScrollRaceFilter, setLastScrollRaceFilter] = useState(
		raceFilter[0]
	);
	const [disableInitialScroll, setDisableInitialScroll] = useState(false);

	const navDrawerRef = useRef<HTMLDivElement | null>(null);

	const matches = useMediaQuery(theme.breakpoints.up('smallLaptop'));
	const matchesMd = useMediaQuery(theme.breakpoints.up('md'));

	useEffect(() => {
		const handleScroll = () => {
			setDisableInitialScroll(true);
			const scrollPosition = window.scrollY;
			const sections = document.querySelectorAll('section');
			const scrollHeight = document.body.scrollHeight;

			const navDrawerRect = navDrawerRef.current?.getBoundingClientRect();
			const distance =
				scrollHeight - scrollPosition - (navDrawerRect?.bottom ?? 0);

			// Check if you are at the end of the page (considering a small buffer, e.g., 10 pixels)
			const isAtEnd = distance <= 400;

			sections.forEach((section, index) => {
				const sectionTop = section.offsetTop;
				const sectionHeight = section.offsetHeight;

				if (
					scrollPosition >=
						sectionTop +
							(matches ? 125 : 241) -
							(matches ? 140 : window.screen.availHeight * 0.5) -
							index * 48 && // Adjust the offset as needed
					scrollPosition <
						sectionTop +
							sectionHeight +
							(matches ? 125 : 241) -
							(matches ? 145 : window.screen.availHeight * 0.5) -
							(index + 1) * 48
					// Adjust the offset as needed
				) {
					const currentLink = links.findIndex(
						(link) => link.href === section.id
					);
					setCurrentNavIndex(isAtEnd ? links.length - 1 : currentLink);
					setSectionAnchor(links[currentLink].href);
				}
			});
			setIsAtEndOfPage(isAtEnd);
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [links, matches, setSectionAnchor, disableInitialScroll]);

	const [initialLoad, setInitialLoad] = useState(true);
	const [initialScrollId, setInitialScrollId] = useState<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		if (!initialLoad && disableInitialScroll) {
			clearTimeout(initialScrollId);
		}
	}, [initialLoad, disableInitialScroll, initialScrollId]);

	useEffect(() => {
		const scrollTo = (delay: number = 1000) => {
			return setTimeout(() => {
				scroller.scrollTo(sectionAnchor, {
					duration: 300,
					offset: matches ? -155 : 5,
					smooth: 'easeInOutCubic'
				});
			}, delay);
		};

		if (initialLoad) {
			if (sectionAnchor !== 'highlights') setInitialScrollId(scrollTo(500));
			setInitialLoad(false);
		} else if (lastScrollState !== selectedState) {
			if (sectionAnchor !== 'highlights') scrollTo();
			setLastScrollState(selectedState);
		} else if (lastScrollElection !== election) {
			if (sectionAnchor !== 'highlights') scrollTo();
			setLastScrollElection(election);
		} else if (lastScrollCongressTab !== congressTab) {
			scrollTo();
			setLastScrollCongressTab(congressTab);
		} else if (lastScrollSeat !== seat[0]) {
			scrollTo();
			setLastScrollSeat(seat[0]);
		} else if (lastScrollRaceFilter !== raceFilter[0]) {
			scrollTo();
			setLastScrollRaceFilter(raceFilter[0]);
		}
	}, [
		sectionAnchor,
		matches,
		initialLoad,
		selectedState,
		lastScrollState,
		election,
		lastScrollElection,
		congressTab,
		lastScrollCongressTab,
		seat,
		lastScrollSeat,
		raceFilter,
		lastScrollRaceFilter
	]);

	return (
		<Box
			ref={navDrawerRef}
			sx={{
				visibility: matchesMd && chapter != 'analysis' ? 'visible' : 'hidden',
				pointerEvents: 'none',
				background: 'transparent',
				width: '25px',
				overflow: 'visible'
			}}
		>
			<Box
				sx={{ marginBottom: '25px', marginTop: '5px', pointerEvents: 'auto' }}
			>
				<NavButton />
			</Box>
			<Box
				sx={{
					visibility: isAtEndOfPage || !matchesMd ? 'hidden' : 'visible',
					pointerEvents: 'auto',
					background: 'transparent'
				}}
			>
				{links.map((link, index) => (
					<SideNavItem
						key={`${index}-${link.label}`}
						link={link}
						chapter={chapter}
						active={
							activeIndex ? index === activeIndex : index === currentNavIndex
						}
					/>
				))}
			</Box>
		</Box>
	);
};
